import { useMemo, useState } from "react";
import { Box, ListSubheader, Menu, MenuItem, TextField } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import type { JSX } from "react";

export interface PickablePerson {
  user_id: number;
  display_name: string;
}

/**
 * In-call "Add person" picker — lists staff (the chat directory) minus anyone
 * already in the call, with a quick filter. Picking one rings them into the
 * call's conference (the server adds them on accept). Anchored above the
 * floating call window (zIndex.modal+1). Presentational — `onPick` does the work.
 */
export function AddPeopleMenu({
  anchorEl,
  onClose,
  people,
  excludeIds,
  onPick,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  people: PickablePerson[];
  excludeIds: number[];
  onPick: (person: PickablePerson) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !exclude.has(p.user_id))
      .filter((p) => !q || p.display_name.toLowerCase().includes(q));
  }, [people, exclude, query]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={anchorEl !== null}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      slotProps={{ paper: { sx: { width: 248, maxHeight: 360 } } }}
    >
      <ListSubheader sx={{ lineHeight: "34px", display: "flex", alignItems: "center", gap: 0.75 }}>
        <PersonAddIcon fontSize="small" /> Add to call
      </ListSubheader>
      <Box sx={{ px: 1, pb: 0.5 }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()} // don't let Menu type-ahead steal keys
          placeholder="Search staff…"
          size="small"
          fullWidth
          autoFocus
          inputProps={{ "aria-label": "Search staff", "data-testid": "addpeople-search" }}
        />
      </Box>
      {candidates.length === 0 && <MenuItem disabled>No one to add</MenuItem>}
      {candidates.map((p) => (
        <MenuItem
          key={p.user_id}
          data-testid={`addpeople-${p.user_id}`}
          onClick={() => onPick(p)}
        >
          {p.display_name}
        </MenuItem>
      ))}
    </Menu>
  );
}
